import { useRef } from "react";
import Form from "../../Reusable/Form/Form.js";
import config from "config";
import useUpdatePasswordRequirements from "hooks/useUpdatePasswordRequirements.ts";
import useCustomLoginErrorHandler from "hooks/useCustomLoginErrorHandler.js";
import useOrganization from "hooks/useOrganization.ts";
import LoginForm from "components/Reusable/LoginForm/index.tsx";
import { signupFields } from "./SignupFields.js";
import { SocialMediaAuthentication } from "../../Structure/SocialMediaAuthentication/SocialMediaAuthentication.js";
import { useContent } from "../../../contexts/contentContext/contentContext.js";
import { useUser } from "../../../contexts/userContext/userContext.js";
import { useToasts } from "react-toast-notifications";
import { useParams } from "react-router-dom";
import { setLocalStorage } from "utils/globalLocalStorage.js";
import { useModal } from "contexts/modalContext/modalContext.js";
import { awaiter } from "components/Pages/Home/Helpers/helpers.js";
import { RECAPTCHA_APPEAR_DELAY } from "utils/globalConsts.ts";
import { CustomDomainPagesE } from "models/enums.ts";

const isInvite =
  window.location.pathname.includes(CustomDomainPagesE.INVITE) ||
  window.location.pathname.includes(CustomDomainPagesE.SUB_USER_COMPLETE);

export const RegistrationSection = ({
  callback = null,
  email,
  autoConfirm,
  inviteDomainName,
  addSubUserToOrganization,
  completeAddSubUser,
  AuthenticationOptions,
  enforceStrongPass,
  children,
}) => {
  const params = useParams();
  const { getTextByKey, getPath } = useContent();
  const { signup, login } = useUser();
  const { closeModal } = useModal();
  const { addToast } = useToasts();

  const { checkCurrDomainIsOrgDomain } = useOrganization();
  const isOrgDomain = checkCurrDomainIsOrgDomain();

  const handleCustomError = useCustomLoginErrorHandler();

  const externalRecaptchaRef = useRef(null);

  const handleCallRecaptcha = async () => {
    const token = await externalRecaptchaRef.current.executeAsync();
    return token;
  };

  const handleSubmit = async (body) => {
    if (params.UserInvitationToken !== undefined) {
      body = {
        ...body,
        RegistrationPage: "react",
        userInvitationToken: params.UserInvitationToken,
      };
    } else {
      body = {
        ...body,
        RegistrationPage: "react",
      };
    }

    if (autoConfirm) {
      body = {
        ...body,
        AutoConfirm: true,
      };
    }

    // body.captchaServiceName = "cloudflare";

    const response = await signup(body);

    if (response.status === "success") {
      addToast(getTextByKey("sign-up-success"), { appearance: "success" });
      if (callback !== null) {
        setLocalStorage("proccessSignUpEmbedPage", true);
      }

      await awaiter(RECAPTCHA_APPEAR_DELAY);
      const recaptchaResponse = await handleCallRecaptcha();

      const loginRes = await login(
        {
          Password: body.Password,
          Remember: false,
          UserName: body.Email,
          recaptchaResponse: recaptchaResponse,
        },
      );

      if (loginRes.status === "success") {
        if (inviteDomainName && addSubUserToOrganization) {
          await addSubUserToOrganization(body.Email);
        }

        if (completeAddSubUser) {
          await completeAddSubUser(body.Email);
          closeModal();
          return;
        }

        if (callback !== null) {
          let tempMessage = { ...loginRes.message };
          tempMessage.IsRegistered = true;
          callback(tempMessage);
        } else {
          window.location.href =
            config.baseurl + getPath() + "registration-complete";
        }
      }
    }
    if (response.status === "error") {
      handleCustomError(response.message);
    }

    return response;
  };

  const signUpFieldsObj = !inviteDomainName
    ? signupFields
    : {
        ...signupFields,
        Email: {
          ...signupFields.Email,
          validation: {
            validate(value) {
              if (!signupFields.Email.validation.reg.test(value)) {
                return getTextByKey(
                  signupFields.Email.validation.invalidMessage
                );
              }
              if (value.includes(inviteDomainName)) {
                return true;
              } else {
                return getTextByKey("use-business-email");
              }
            },
          },
        },
      };

  const signUpFormFields = useUpdatePasswordRequirements({
    defaultFields: signUpFieldsObj,
    fieldName: "Password",
    confirmField: "PasswordValidation",
    enforceStrongPass,
  });

  return (
    <div
      className={`registration-section ${
        AuthenticationOptions && !AuthenticationOptions?.Email
          ? "registration-section-social-only"
          : ""
      }`}
    >
      <div className="title">
        {getTextByKey(isOrgDomain && !isInvite ? "sign-in" : "signup")}
      </div>
      {(AuthenticationOptions ? AuthenticationOptions?.Email : true) &&
        (!isOrgDomain || isInvite) && (
          <Form
            fieldsProps={{ label: "hidden", field: "square" }}
            buttonProps={{ buttonsSize: "large" }}
            fields={signUpFormFields}
            onSubmit={handleSubmit}
            defaultValuesObj={{ Email: email || "" }}
            submitProps={{ title: getTextByKey("create-free-account") }}
          />
        )}
      {isOrgDomain && !isInvite && AuthenticationOptions?.Email ? (
        <LoginForm asRootUser />
      ) : null}
      {inviteDomainName ||
      (checkCurrDomainIsOrgDomain() && !isInvite) ? null : (
        <>
          {AuthenticationOptions?.Email &&
          (AuthenticationOptions?.GooglSSO ||
            AuthenticationOptions?.MicrosoftSSO) ? (
            <div className="horizontal-or-divider">
              <div className="divider-text">{getTextByKey("or")}</div>
            </div>
          ) : null}
          <SocialMediaAuthentication
            callback={callback}
            AuthenticationOptions={AuthenticationOptions}
          />
        </>
      )}
      {inviteDomainName &&
        AuthenticationOptions &&
        !AuthenticationOptions?.Email && (
          <p className="sub-user-join-hint">
            {getTextByKey("sub-user-join-hint").replace(
              "%domain%",
              inviteDomainName
            )}
          </p>
        )}
      {inviteDomainName &&
        (AuthenticationOptions?.GoogleSSO ||
          AuthenticationOptions?.MicrosoftSSO) && (
          <SocialMediaAuthentication
            callback={callback}
            AuthenticationOptions={AuthenticationOptions}
            addSubUserToOrganization={addSubUserToOrganization}
            inviteDomainName={inviteDomainName}
          />
        )}
      {children}
    </div>
  );
};
